import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles, Phone, Globe } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useLang } from "@/i18n";
import { API } from "@/lib/apiClient";

export const AIChat = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).slice(2));
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);
  useEffect(() => {
    if (open && messages.length === 0) setMessages([{ id: "welcome", role: "assistant", content: t.ai.welcome }]);
  }, [open]); // eslint-disable-line

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    const uid = Date.now();
    setMessages((m) => [...m, { id: `u${uid}`, role: "user", content: text }, { id: `a${uid}`, role: "assistant", content: "" }]);
    setLoading(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, lang }),
      });
      if (!resp.ok || !resp.body) throw new Error("bad response");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          let data;
          try { data = JSON.parse(line.slice(6)); } catch { continue; }
          if (data.error) throw new Error(data.error);
          if (data.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", content: copy[copy.length - 1].content + data.delta };
              return copy;
            });
          }
          if (data.done && data.sources?.length) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], sources: data.sources };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      console.error("AI chat error:", e);
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", content: "⚠️ " + t.contact.notSure + " 067/87.05.53" };
        return copy;
      });
    } finally { setLoading(false); }
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1, type: "spring" }}
        onClick={() => setOpen(true)} data-testid="ai-chat-open"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-accent text-white pl-4 pr-5 py-3.5 accent-glow hover:scale-105 transition-transform">
        <Sparkles className="w-5 h-5" strokeWidth={1.5} />
        <span className="text-sm font-medium hidden sm:inline">{t.hero.cta2}</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.96 }}
            className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-[400px] z-50 h-[560px] max-h-[85vh] glass rounded-2xl flex flex-col overflow-hidden accent-glow"
            data-testid="ai-chat-panel">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-accent/20 flex items-center justify-center"><Sparkles className="w-4.5 h-4.5 text-accent" strokeWidth={1.5} /></div>
                <div>
                  <p className="text-sm font-semibold">{t.ai.title}</p>
                  <p className="text-xs text-green-400 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />{t.ai.online}</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ai-chat-close" className="text-muted-foreground hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((m, i) => (
                <div key={m.id || i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-accent text-white rounded-br-sm whitespace-pre-wrap" : "bg-white/5 text-gray-100 rounded-bl-sm chat-md"}`}>
                    {m.role === "assistant" ? (
                      m.content
                        ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                        : (loading && i === messages.length - 1 ? <span className="text-muted-foreground">{t.ai.thinking}</span> : "")
                    ) : m.content}
                  </div>
                  {m.sources?.length > 0 && (
                    <div className="mt-2 max-w-[85%] flex flex-wrap gap-1.5" data-testid={`ai-sources-${i}`}>
                      {m.sources.map((s, j) => {
                        let host = s.url;
                        try { host = new URL(s.url).hostname.replace("www.", ""); } catch { /* keep raw */ }
                        return (
                          <a key={j} href={s.url} target="_blank" rel="noopener noreferrer" title={s.title}
                            className="flex items-center gap-1 text-[10px] text-accent bg-accent/10 border border-accent/20 rounded-full px-2 py-1 hover:bg-accent/20 transition-colors">
                            <Globe className="w-2.5 h-2.5" strokeWidth={1.5} /> [{j + 1}] {host}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <div className="p-3 border-t border-white/10">
              <div className="flex items-center gap-2">
                <input
                  value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t.ai.placeholder} data-testid="ai-chat-input"
                  className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm outline-none focus:border-accent transition-colors" />
                <button onClick={send} disabled={loading} data-testid="ai-chat-send"
                  className="w-10 h-10 shrink-0 rounded-full bg-accent flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-50">
                  <Send className="w-4 h-4 text-white" strokeWidth={1.5} />
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground/60 mt-2 flex items-center gap-1 px-1"><Phone className="w-2.5 h-2.5" /> {t.ai.disclaimer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
